import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getAllEmailTypes,
  getCSVWatchStatus,
  getClickEmailsByIdAndDateRange,
  getEmailDetailsById,
  getEmailQueueWatchers,
  getEmailSendingHistory,
  getOneCSVSummary,
  getTemplateMetricsByIdAndDateRange,
  getThrottleExemptionList,
  validateCSVMail,
} from 'services/mail';
import { getCategories } from '../services/categories';
import * as _ from 'lodash';
import { useState } from 'react';

// eslint-disable-next-line import/prefer-default-export
export const useValidateCSVMail = (
  file,
  templateId,
  isMultiMode,
  forceChoose,
) => {
  return useQuery(
    ['validate-csv-mail', { file, templateId, isMultiMode, forceChoose }],
    () => {
      const formData = new FormData();
      formData.append(
        'payload',
        JSON.stringify({
          template_id: templateId,
          isMultiCSV: isMultiMode,
          force_choose: forceChoose,
        }),
      );
      formData.append('csv_file', file);
      return validateCSVMail(formData);
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: !!file && (!!templateId || !!isMultiMode),
      networkMode: 'always',
    },
  );
};

export const useGetAllEmailTypes = () => {
  return useQuery(['emailTypes'], getAllEmailTypes, {
    initialData: [],
  });
};

export const useGetAllCategories = () => {
  return useQuery(['categories'], getCategories, {
    initialData: [],
  });
};

export const useGetCSVWatchStatus = id => {
  return useQuery(
    ['get-csv-watch-status', { id }],
    () => getCSVWatchStatus(id),
    {
      enabled: !!id,
      refetchOnMount: true,
      initialData: {},
    },
  );
};

export const useGetOneCSVSummary = id => {
  return useQuery(['get-one-csv-summary', { id }], () => getOneCSVSummary(id), {
    enabled: !!id,
    refetchOnMount: 'always',
    refetchInterval: data => {
      if (!data?.isAborted && data?.status !== 'completed') {
        return 1000 * 30;
      }
      return 1000 * 60 * 5;
    },
  });
};

type GetSentEmailsOpt = {
  startDate?: Date,
  endDate?: Date,
  templateIds?: number[],
  sender?: string,
  receiver?: string,
  page?: number,
  limit?: number,
  emailId?: string,
};

export const useGetEmailSendingHistory = (opts: GetSentEmailsOpt) => {
  const queryClient = useQueryClient();
  const queryKey = [
    'sending-history',
    JSON.stringify(_.omit(opts, 'excludeCache')),
  ];
  const query = useQuery(queryKey, () => getEmailSendingHistory(opts), {
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const [isRefetching, setIsRefetching] = useState(false);
  const onRefetch = async () => {
    if (isRefetching) return;
    setIsRefetching(true);
    try {
      const data = await getEmailSendingHistory({
        ...opts,
        excludeCache: true,
      });
      queryClient.removeQueries(['sending-history']);
      queryClient.setQueryData(queryKey, data);
    } catch (e) {
      // Ignore
    }
    setIsRefetching(false);
  };
  return {
    ...query,
    isLoading: query.isLoading || isRefetching,
    refetch: onRefetch,
  };
};

export const useGetEmailDetailsById = id => {
  return useQuery(['email-details', { id }], () => getEmailDetailsById(id), {
    enabled: !!id,
    refetchOnMount: true,
  });
};

export const useGetTemplatesMetricsByIdAndDateRange = (
  id,
  startDate,
  endDate,
) => {
  return useQuery(
    ['template-metrics', { id, startDate, endDate }],
    () =>
      getTemplateMetricsByIdAndDateRange(id, {
        startDate,
        endDate,
      }),
    {
      enabled: !!id && !!startDate && !!endDate,
      refetchOnMount: true,
    },
  );
};

export const useGetEmailQueueWatcher = () => {
  return useQuery(['email-queue-watcher'], getEmailQueueWatchers, {
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetClickEmailsByIdAndDateRange = ({
  id,
  startDate,
  endDate,
  enabled,
}) => {
  return useInfiniteQuery(
    ['template-click-emails', { id, startDate, endDate }],
    ({ pageParam }) =>
      getClickEmailsByIdAndDateRange(id, {
        startDate,
        endDate,
        limit: 100,
        cursor: pageParam,
      }),
    {
      getNextPageParam: lastPage => lastPage.nextCursor,
      enabled,
      refetchOnMount: true,
    },
  );
};

export const useGetThrottleExemptionList = () => {
  return useQuery(['throttle-exemption-list'], getThrottleExemptionList, {
    refetchOnMount: true,
    initialData: [],
  });
};
